import {
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { fetchApi } from "..";
import {
  MentorTrainingProgressStatus,
  TrainingAttempt,
  TrainingAttemptThread,
  TrainingEvent,
  TrainingHomeModule,
  TrainingLevelTeen,
  TrainingMessage,
} from "./training-simulator";
import { useToast } from "@/components/ui/use-toast";

export const getTrainingReviewHomeKey = (search?: string) =>
  ["training-simulator-review", "home", search] as const;

export interface ReviewMentor {
  id: string;
  name: string;
  profile_picture_url: string | null;
}
export interface ReviewListRow {
  progressId: string;
  mentor: ReviewMentor;
  status: MentorTrainingProgressStatus;
  completed_at: string | null;
  joined_at: string | null;
}

export interface ReviewListResponse {
  counts: {
    in_progress: number;
    needs_review: number;
    approved: number;
    in_training: number;
    awaiting_review: number;
  };
  in_progress: ReviewListRow[];
  needs_review: ReviewListRow[];
  approved: ReviewListRow[];
}

export const useTrainingSimulatorReviewHome = (searchTerm?: string) => {
  return useQuery({
    queryKey: getTrainingReviewHomeKey(searchTerm),
    queryFn: async () => {
      const { data } = await fetchApi<ReviewListResponse>({
        url: "v2/training-simulator/reviews",
        params: {
          ...(searchTerm?.trim() ? { query: searchTerm } : {}),
        },
      });

      return data;
    },
  });
};

type ReviewDetailTeen = Pick<
  TrainingLevelTeen,
  "id" | "display_name" | "age" | "gender" | "pronouns" | "profile"
>;

interface ReviewDetailThread {
  thread: TrainingAttemptThread;
  teen: ReviewDetailTeen | null;
  messages: TrainingMessage[];
  events: TrainingEvent[];
}

export interface ReviewDetailLevel
  extends Pick<
    TrainingAttempt["level"],
    "id" | "position" | "title" | "summary" | "context_copy" | "mentor_goal"
  > {
  latestAttempt: TrainingAttempt | null;
  threads: ReviewDetailThread[];
}

export interface ReviewDetailModule
  extends Pick<
    TrainingHomeModule,
    "id" | "position" | "title" | "description" | "metadata"
  > {
  levels: ReviewDetailLevel[];
}

export enum AiFeedbackRating {
  THUMBS_UP = "thumbs_up",
  THUMBS_DOWN = "thumbs_down",
}

export interface Review {
  ai_feedback: string | null;
  ai_feedback_rating: AiFeedbackRating | null;
  lead_mentor_feedback: string | null;
  submitted_at: string | null;
  approved_at: string | null;
  approved_by: string | null;
}

export interface ReviewDetailResponse {
  mentor: ReviewMentor;
  status: MentorTrainingProgressStatus;
  modules: ReviewDetailModule[];
  review: Review | null;
  joined_at: string | null;
}

export const getTrainingReviewProgressKey = (progressId: string) =>
  ["training-simulator-review", progressId] as const;

function setReviewInCache(
  queryClient: QueryClient,
  progressId: string,
  review: Review,
) {
  queryClient.setQueryData<ReviewDetailResponse>(
    getTrainingReviewProgressKey(progressId),
    (old) => (old ? { ...old, review } : old),
  );
}

export const useTrainingSimulatorReview = (progressId: string) => {
  return useQuery({
    queryKey: getTrainingReviewProgressKey(progressId),
    queryFn: async () => {
      const { data } = await fetchApi<ReviewDetailResponse>({
        url: `v2/training-simulator/reviews/${progressId}`,
      });

      return data;
    },
    enabled: !!progressId,
  });
};

export const useAiFeedbackRating = (progressId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["ai-feedback-rating", progressId],
    mutationFn: async ({ rating }: { rating: AiFeedbackRating }) => {
      const { data } = await fetchApi<Review>({
        method: "POST",
        url: `v2/training-simulator/reviews/${progressId}/ai-feedback-rating`,
        data: {
          rating,
        },
      });

      return data;
    },
    onMutate: async ({ rating }) => {
      const queryKey = getTrainingReviewProgressKey(progressId);
      await queryClient.cancelQueries({ queryKey });

      const previousReview =
        queryClient.getQueryData<ReviewDetailResponse>(queryKey);

      queryClient.setQueryData<ReviewDetailResponse>(queryKey, (old) => {
        if (!old) return old;

        return {
          ...old,
          review: {
            ...old.review,
            ai_feedback_rating: rating,
          } as Review,
        };
      });

      return { previousReview };
    },
    onError: (_err, _data, context) => {
      const queryKey = getTrainingReviewProgressKey(progressId);

      if (context?.previousReview) {
        queryClient.setQueryData(queryKey, context.previousReview);
      }
    },
    onSuccess: (review) => {
      setReviewInCache(queryClient, progressId, review);
    },
  });
};

export const useSubmitFeedback = (progressId: string) => {
  const toast = useToast();
  const queryClient = useQueryClient();

  return useMutation({
    mutationKey: ["feedback", progressId],
    mutationFn: async ({ feedback }: { feedback: string }) => {
      const { data } = await fetchApi<Review>({
        method: "POST",
        url: `v2/training-simulator/reviews/${progressId}/feedback`,
        data: {
          feedback,
        },
      });

      return data;
    },
    onSuccess: () => {
      toast.toast({
        title: "Success",
        description: "Feedback submitted successfully",
      });
    },
    onError: () => {
      toast.toast({
        title: "Something went wrong!",
        description: "Please try again later",
        variant: "destructive",
      });
    },
    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: getTrainingReviewProgressKey(progressId),
      });
    },
  });
};

export const useSendMentorMessage = (mentorId: string) => {
  return useMutation({
    mutationKey: ["training-simulator", "mentor-message", mentorId],
    mutationFn: async ({ message }: { message: string }) => {
      const { data } = await fetchApi<unknown>({
        url: `v2/training-simulator/mentor-message/${mentorId}`,
        method: "POST",
        data: { message },
      });

      return data;
    },
  });
};

export const useApproveMentorTraining = (progressId: string) => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["approve-mentor", progressId],
    mutationFn: async () => {
      const { data } = await fetchApi({
        url: `v2/training-simulator/reviews/${progressId}/approve`,
        method: "POST",
      });

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["training-simulator-review"],
      });
      toast({
        title: "Review approved successfully!",
        description: "Mentor is now ready to be onboarded",
      });
    },
    onError: () => {
      toast({
        title: "Something went wrong!",
        description: "Please try again later",
        variant: "destructive",
      });
    },
  });
};

export const useBulkApproveMentorTraining = () => {
  const { toast } = useToast();
  const queryClient = useQueryClient();
  return useMutation({
    mutationKey: ["bulk-approve-mentor-training"],
    mutationFn: async ({ progressIds }: { progressIds: string[] }) => {
      const { data } = await fetchApi({
        url: `v2/training-simulator/reviews/bulk-approve`,
        method: "POST",
        data: {
          progressIds,
        },
      });

      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: ["training-simulator-review"],
      });
      toast({
        title: "All reviews approved successfully!",
        description: "Mentors are now ready to be onboarded",
      });
    },
    onError: () => {
      toast({
        title: "Something went wrong!",
        description: "Please try again later",
        variant: "destructive",
      });
    },
  });
};
